// Maps GET /api/market/news items -- third-party headlines shaped
// { title, date, source, url, body, image } -- into the cards and ticker
// headlines the landing page's IPO News section renders. The feed carries no
// category or tag fields, so both are derived from the headline text.

const GMP_PATTERN = /\bgmp\b|grey market/i;
const SEBI_PATTERN = /\bsebi\b|\bu?drhp\b|\brhp\b|draft (red herring|papers|prospectus)|\bfil(es|ed|ing)\b/i;
const ANALYSIS_PATTERN =
  /\breview\b|should you|apply or (not|skip|avoid)|prediction|explained|analys[ie]s|analyst|outlook|\bvs\.?\s|compared|valuation/i;

// First match wins for the card's category badge and header colour
const CATEGORY_RULES = [
  { label: 'GMP Update', pattern: GMP_PATTERN, gradient: 'from-purple-600 to-pink-600' },
  { label: 'SEBI Filing', pattern: SEBI_PATTERN, gradient: 'from-emerald-500 to-teal-700' },
  { label: 'Subscription', pattern: /subscri|anchor|\bqibs?\b|bidding/i, gradient: 'from-indigo-700 to-blue-900' },
  { label: 'SME IPO', pattern: /\bsme\b/i, gradient: 'from-amber-500 to-orange-600' },
  { label: 'Analysis', pattern: ANALYSIS_PATTERN, gradient: 'from-sky-600 to-indigo-700' },
  { label: 'Listing', pattern: /\blist(s|ed|ing)\b|debut/i, gradient: 'from-teal-600 to-cyan-700' },
];
const DEFAULT_CATEGORY = { label: 'IPO News', gradient: 'from-slate-600 to-slate-800' };

export const NEWS_FILTERS = [
  { key: 'all', label: 'All News', test: () => true },
  { key: 'sebi', label: 'SEBI Filings', test: (text) => SEBI_PATTERN.test(text) },
  { key: 'gmp', label: 'GMP Buzz', test: (text) => GMP_PATTERN.test(text) },
  { key: 'analysis', label: 'Analysis', test: (text) => ANALYSIS_PATTERN.test(text) },
];

// Third-party feed: only ever render http(s) links and images
const safeUrl = (value) => (/^https?:\/\//i.test(String(value || '')) ? value : null);

// "The Economic Times on MSN" -> "The Economic Times", "Bloomberg via Yahoo News" -> "Bloomberg"
const cleanSource = (source) =>
  String(source || '')
    .replace(/\s*(?:·\s*)?\b(?:on|via)\s+(?:MSN|Yahoo(?:\s+\w+)?)\s*$/i, '')
    .trim();

const UNIT_LABELS = { crore: 'Cr', cr: 'Cr', lakh: 'Lakh', million: 'M', mn: 'M', billion: 'B', bn: 'B' };
const AMOUNT_PATTERN = /(₹|\brs\.?|\$)\s?([\d,]+(?:\.\d+)?)[\s-]*(crore|cr|lakh|million|mn|billion|bn)\b/i;

// "₹2,495 crore" -> "₹2,495 Cr", "Rs 22,569-crore" -> "₹22,569 Cr", "$1.3 Billion" -> "$1.3B"
const extractAmount = (text) => {
  const match = String(text || '').match(AMOUNT_PATTERN);
  if (!match) return null;
  const [, currency, amount, unit] = match;
  const symbol = currency === '$' ? '$' : '₹';
  const label = UNIT_LABELS[unit.toLowerCase()];
  return label.length === 1 ? `${symbol}${amount}${label}` : `${symbol}${amount} ${label}`;
};

export const formatNewsTime = (iso, now = Date.now()) => {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return '';
  const minutes = Math.floor((now - time) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const date = new Date(time);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
};

/**
 * @param {object} item - one entry of GET /api/market/news -> data.items
 */
export const mapNewsItem = (item) => {
  const title = String(item?.title || '').trim();
  if (!title) return null;
  const snippet = String(item.body || '').trim();
  const text = `${title} ${snippet}`;
  const category = CATEGORY_RULES.find((rule) => rule.pattern.test(title)) || DEFAULT_CATEGORY;
  const timestamp = Date.parse(item.date);

  return {
    id: item.url || title,
    title,
    snippet,
    url: safeUrl(item.url),
    image: safeUrl(item.image),
    source: cleanSource(item.source) || 'News Desk',
    publishedAt: item.date || null,
    timestamp: Number.isFinite(timestamp) ? timestamp : 0,
    category: category.label,
    gradient: category.gradient,
    tag: extractAmount(title) || extractAmount(snippet),
    filters: NEWS_FILTERS.filter((filter) => filter.test(text)).map((filter) => filter.key),
  };
};

// Newest first, without repeats of the same story
export const mapNewsItems = (items) => {
  const seen = new Set();
  return (items || [])
    .map(mapNewsItem)
    .filter((article) => {
      if (!article || seen.has(article.id)) return false;
      seen.add(article.id);
      return true;
    })
    .sort((a, b) => b.timestamp - a.timestamp);
};

// ---------------------------------------------------------------------------
// Maps company_news items (backend/services/extraction_svc/company_logo.py::
// attach_company_news writes {title, date, source, url, body} onto
// json_data.basic_company_details.company_news) into NewsCard's article
// shape. Cycled, not per-category, since real news has no fixed taxonomy
// the way the old mock data's "SEBI APPROVAL"/"GMP UPDATE" labels implied.
// ---------------------------------------------------------------------------

const GRADIENTS = [
  'from-amber-500 to-orange-600',
  'from-emerald-500 to-teal-700',
  'from-indigo-700 to-blue-900',
  'from-purple-600 to-pink-600',
];
const TAG_COLORS = ['emerald', 'blue', 'purple'];

const estimateReadTime = (body) => {
  const words = (body || '').trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
};

/**
 * @param {{id: number|string, company_name: string}} ipo
 * @param {object[]} newsItems - raw company_news entries for that IPO
 * @param {number} startIndex - running index across all IPOs, for gradient/tag cycling
 */
export const mapCompanyNewsToArticles = (ipo, newsItems, startIndex = 0) =>
  (newsItems || [])
    .filter((item) => item?.title)
    .map((item, idx) => {
      const cycle = startIndex + idx;
      return {
        id: `${ipo.id}-${idx}`,
        category: (ipo.company_name || 'IPO').toUpperCase(),
        readTime: estimateReadTime(item.body),
        gradient: GRADIENTS[cycle % GRADIENTS.length],
        title: item.title,
        snippet: item.body || 'No summary available.',
        source: item.source || 'Web',
        time: item.date || 'Recent',
        tag: ipo.company_name || 'IPO News',
        tagColor: TAG_COLORS[cycle % TAG_COLORS.length],
        url: item.url || '',
      };
    });
